'use client';

// Who owns a touch: the element, or the page?
//
// A phone hands vertical panning to the browser by default, and the browser
// takes it MID-DRAG: the pointer stream stops dead (`pointercancel`) the moment
// a finger drifts a few pixels off the horizontal, which every real thumb
// does. That is how every chart in the graph dock except the frequency
// response used to behave — a horizontal scrub that died on the first pixel
// of vertical wobble — while working perfectly with a mouse.
//
// `touch-action: none` is the blunt fix and the right one where the host has
// nothing to scroll (a pill floating over the 3D view). The 2D plan editors
// split by what is under the finger instead: see useTouchGrab.
// It is the wrong one inside a scrollable panel: a dock of ten charts would
// stop scrolling wherever a finger happened to land on a plot. So this hook
// offers both, behind one interface:
//
//   'page-scroll' (default) — the element keeps `touch-action: pan-y` and
//     splits the gesture by DIRECTION. The first `INTENT_PX` of travel decide
//     it: mostly horizontal is ours, mostly vertical is the page's. Once
//     claimed a gesture stays claimed however the thumb tilts afterwards
//     (that is the whole point — the wobble is noise, not intent), and a
//     touch that never travels is a tap, which is also ours. The browser
//     confirms a vertical pan by firing `pointercancel`, which this reports
//     as `onCancel`.
//   'capture' — `touch-action: none`; every touch is ours from the down.
//
// A mouse or a pen never has to earn the gesture: it is claimed on press, and
// its unpressed movement is reported as a hover.
//
// The direction test lives on a hand-registered, non-passive `touchmove`
// listener rather than an `onTouchMove` prop: React attaches touchmove
// passively at the root, where `preventDefault()` does nothing. This is the
// mechanism BeforeAfter.tsx's room wipe proved on the landing page, extracted
// so every horizontal control shares one implementation.

import { useCallback, useEffect, useRef, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';

export type PointerGestureMode = 'page-scroll' | 'capture';

export interface GesturePoint {
  clientX: number;
  clientY: number;
  pointerType: string;
}

export interface ScrollSafePointerOptions {
  /** The element the returned handlers go on. It also takes the native
   *  touchmove listener, so it must be the same node. */
  ref: RefObject<HTMLElement | null>;
  mode?: PointerGestureMode;
  /** The pointer is at `p`. `claimed` is true once the gesture is ours: on
   *  press for a mouse/pen, after the direction is known (or on the tap's
   *  release) for a touch. An unclaimed report is a hover. */
  onPoint: (p: GesturePoint, claimed: boolean) => void;
  /** A claimed gesture ended with the pointer released — a drag's end, or a
   *  tap. Fires after the tap's own `onPoint(p, true)`. */
  onRelease?: (p: GesturePoint) => void;
  /** The browser took the gesture (a page scroll), the pointer was lost, or
   *  a touch that had been judged the page's was released. Whatever the
   *  press showed should be withdrawn. */
  onCancel?: () => void;
  /** A mouse or pen left the element. A touch never "leaves": its
   *  pointerleave (fired after every touch's release) is swallowed so a tap
   *  can leave its mark on screen. */
  onLeave?: () => void;
}

export interface ScrollSafePointerHandlers {
  onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerLeave: (e: ReactPointerEvent<HTMLElement>) => void;
}

/** Travel before a touch's direction is knowable. Under this the gesture is
 *  nobody's yet; claiming earlier would take a page scroll away on jitter. */
export const INTENT_PX = 6;

interface ActiveGesture {
  pointerId: number;
  x0: number;
  y0: number;
  claimed: boolean;
  /** Judged the page's by direction. Kept until the browser's own
   *  pointercancel (or the release) so nothing fires twice. */
  abandoned: boolean;
}

function point(e: { clientX: number; clientY: number; pointerType: string }): GesturePoint {
  return { clientX: e.clientX, clientY: e.clientY, pointerType: e.pointerType };
}

export function useScrollSafePointer(opts: ScrollSafePointerOptions): {
  handlers: ScrollSafePointerHandlers;
  style: { touchAction: string };
} {
  const mode = opts.mode ?? 'page-scroll';
  const active = useRef<ActiveGesture | null>(null);
  // Callbacks read through a ref so the native listener below registers once
  // and never sees a stale closure.
  const optsRef = useRef(opts);
  optsRef.current = opts;

  useEffect(() => {
    const el = opts.ref.current;
    if (!el) return;
    const onTouchMove = (e: TouchEvent) => {
      const g = active.current;
      if (!g || g.abandoned) return;
      if (!g.claimed) {
        const t = e.touches[0];
        if (!t) return;
        const dx = Math.abs(t.clientX - g.x0);
        const dy = Math.abs(t.clientY - g.y0);
        if (Math.max(dx, dy) < INTENT_PX) return;
        if (dy > dx) {
          g.abandoned = true;
          return;
        }
        g.claimed = true;
        optsRef.current.onPoint({ clientX: t.clientX, clientY: t.clientY, pointerType: 'touch' }, true);
      }
      // Keep preventing for the rest of the gesture: a scroll can still start
      // on any later move let through.
      if (e.cancelable) e.preventDefault();
    };
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => el.removeEventListener('touchmove', onTouchMove);
  }, [opts.ref]);

  const release = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // Already released — the browser may have dropped the capture with the
      // pointercancel that got us here.
    }
  }, []);

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    // Only the primary button: middle/right are the browser's own gestures.
    if (e.button !== 0) return;
    const claimed = mode === 'capture' || e.pointerType !== 'touch';
    active.current = { pointerId: e.pointerId, x0: e.clientX, y0: e.clientY, claimed, abandoned: false };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // The pointer can already be gone; the move handler still fires inside
      // the element.
    }
    optsRef.current.onPoint(point(e), claimed);
  }, [mode]);

  const onPointerMove = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    const g = active.current;
    if (!g) {
      // Unpressed movement: a hover. A finger cannot hover.
      if (e.pointerType !== 'touch') optsRef.current.onPoint(point(e), false);
      return;
    }
    if (g.pointerId !== e.pointerId || !g.claimed) return;
    optsRef.current.onPoint(point(e), true);
  }, []);

  const onPointerUp = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    const g = active.current;
    if (!g || g.pointerId !== e.pointerId) return;
    active.current = null;
    release(e);
    if (g.abandoned) {
      optsRef.current.onCancel?.();
      return;
    }
    if (!g.claimed) {
      // A touch that never travelled: a tap, which is ours after all.
      optsRef.current.onPoint(point(e), true);
    }
    optsRef.current.onRelease?.(point(e));
  }, [release]);

  const onPointerCancel = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    const g = active.current;
    if (!g || g.pointerId !== e.pointerId) return;
    active.current = null;
    release(e);
    optsRef.current.onCancel?.();
  }, [release]);

  const onPointerLeave = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    if (e.pointerType === 'touch' || active.current) return;
    optsRef.current.onLeave?.();
  }, []);

  return {
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onPointerLeave },
    // `pinch-zoom` is spelt out because `pan-y` alone would also cost the
    // element the browser's own zoom.
    style: { touchAction: mode === 'capture' ? 'none' : 'pan-y pinch-zoom' },
  };
}
